import React, {
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineDelete, AiOutlineEdit, AiOutlineEye } from 'react-icons/ai';
import { MdToggleOff, MdToggleOn } from 'react-icons/md';

import * as S from './styles';
import { color } from 'styles/pallete';
import AddBtn from 'components/AddBtn';
import ConfirmScreen from 'components/ConfirmScreen';
import FormBannerPopup from 'components/FormBannerPopup';
import Carousel from 'components/Carousel';
import Load from 'components/Load';
import Modal from 'components/Modal';
import TitleNavBarContext from 'context/TitleNavBarContext';
import SystemNotifyContext from 'context/SystemNotifyContext';
import LoadGeneralContext from 'context/LoadGeneralContext';
import { UseTranslationProps } from 'types/UseTranslationProps';
import { BannerPopupProps } from 'types/BannerPopupProps';
import {
	deleteBannerPopupReq,
	editBannerPopupReq,
	getBannerPopups,
	isBannerPopupInsideValidity
} from './reqs';

function formatDate(date: string) {
	if (date === '') {
		return '-';
	}

	const dateValue = date.includes('T') ? date : `${date}T00:00:00`;

	return new Date(dateValue)
		.toLocaleString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		})
		.replace(',', '');
}

function getSegmentation(item: BannerPopupProps) {
	const total = item.distributorIds?.length || 0;

	return total === 0 ? 'Sem segmentação' : `${total} segmento(s)`;
}

function getTypeLabel(type: BannerPopupProps['type']) {
	return type === 'COUNTDOWN' ? 'PopUp com Contador' : 'PopUp Simples';
}

function getStatusLabel(item: BannerPopupProps) {
	if (item.status === 'ACTIVE' && !isBannerPopupInsideValidity(item)) {
		return 'Expirado';
	}

	return item.status === 'ACTIVE' ? 'Ativo' : 'Inativo';
}

export default function BannerPopup() {
	const { i18n }: UseTranslationProps = useTranslation();
	const { setDataAbouTitle } = useContext(TitleNavBarContext);
	const { handleNotify } = useContext(SystemNotifyContext);
	const { setIsOpenLoad, setMessage } = useContext(LoadGeneralContext);
	const handleNotifyRef = useRef(handleNotify);

	const [isLoad, setIsLoad] = useState(false);
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [isOpenBoxToDelete, setIsOpenToDelete] = useState(false);
	const [bannerToEdit, setBannerToEdit] = useState<BannerPopupProps | null>(
		null
	);
	const [bannerToPreview, setBannerToPreview] =
		useState<BannerPopupProps | null>(null);
	const [bannerToDelete, setBannerToDelete] = useState<BannerPopupProps | null>(
		null
	);
	const [listBanners, setListBanners] = useState<BannerPopupProps[]>([]);

	function openEdit(item: BannerPopupProps) {
		setBannerToEdit(item);
		setIsFormOpen(true);
	}

	async function toggleStatus(item: BannerPopupProps) {
		if (
			item.status === 'INACTIVE' &&
			listBanners.some(
				(banner) =>
					banner.status === 'ACTIVE' && isBannerPopupInsideValidity(banner)
			)
		) {
			handleNotify('Ja existe outro Banner PopUp ativo.', 'error');
			return;
		}

		const updatedBanner = {
			...item,
			status: item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
		} as BannerPopupProps;

		const { isSuccess } = await editBannerPopupReq(
			item,
			updatedBanner,
			setIsOpenLoad,
			setMessage,
			handleNotify
		);

		if (isSuccess) {
			setListBanners((list) =>
				list.map((banner) => (banner.id === item.id ? updatedBanner : banner))
			);
		}
	}

	async function deleteBanner() {
		if (!bannerToDelete) {
			return false;
		}

		const { isSuccess } = await deleteBannerPopupReq(
			bannerToDelete.id,
			setIsOpenLoad,
			setMessage,
			handleNotify
		);

		if (isSuccess) {
			setListBanners((list) =>
				list.filter((item) => item.id !== bannerToDelete.id)
			);
			setBannerToDelete(null);
			setIsOpenToDelete(false);
		}

		return isSuccess;
	}

	useEffect(() => {
		handleNotifyRef.current = handleNotify;
	}, [handleNotify]);

	useEffect(() => {
		(async () => {
			setIsLoad(true);
			const { isSuccess, list } = await getBannerPopups(
				handleNotifyRef.current
			);
			setIsLoad(false);

			if (isSuccess) {
				setListBanners(list);
			}
		})();
	}, []);

	useLayoutEffect(() => {
		setDataAbouTitle((value) => ({
			...value,
			title: 'Banner PopUp'
		}));
	}, [i18n?.language, setDataAbouTitle]);

	return (
		<S.container>
			<S.header style={{ display: 'none' }}>
				<AddBtn
					anyFunction={() => {
						setBannerToEdit(null);
						setIsFormOpen(true);
					}}
					text="Adicionar Banner PopUp"
				/>
			</S.header>

			{isLoad ? (
				<S.centralize>
					<Load showText text="Buscando Banner PopUps" />
				</S.centralize>
			) : (
				<S.listContent>
					{listBanners.length === 0 ? (
						<S.emptyList>
							<p>Não ha Banner PopUps cadastrados</p>
						</S.emptyList>
					) : (
						<S.table>
							<thead>
								<tr>
									<th>Nome</th>
									<th>Tipo</th>
									<th>Status</th>
									<th>Data início</th>
									<th>Data término</th>
									<th>Segmentação</th>
									<th>Ações</th>
								</tr>
							</thead>
							<tbody>
								{listBanners.map((item) => (
									<tr key={item.id}>
										<td>{item.name}</td>
										<td>
											<S.type>{getTypeLabel(item.type)}</S.type>
										</td>
										<td>
											<S.status
												$active={
													item.status === 'ACTIVE' &&
													isBannerPopupInsideValidity(item)
												}
											>
												{getStatusLabel(item)}
											</S.status>
										</td>
										<td>{formatDate(item.startDate)}</td>
										<td>{formatDate(item.endDate)}</td>
										<td>{getSegmentation(item)}</td>
										<td>
											<S.actions>
												<button
													data-testid={`banner-popup-preview-${item.id}`}
													title="Visualizar"
													onClick={() => {
														setBannerToPreview(item);
														setIsPreviewOpen(true);
													}}
												>
													<AiOutlineEye />
												</button>
												<button
													data-testid={`banner-popup-edit-${item.id}`}
													title="Editar"
													onClick={() => openEdit(item)}
												>
													<AiOutlineEdit />
												</button>
												<button
													data-testid={`banner-popup-toggle-${item.id}`}
													onClick={() => toggleStatus(item)}
												>
													{item.status === 'ACTIVE' ? (
														<MdToggleOn title="Ativo" />
													) : (
														<MdToggleOff
															title="Inativo"
															color={color.interface.lightGray}
														/>
													)}
												</button>
												<button
													data-testid={`banner-popup-delete-${item.id}`}
													title="Excluir"
													onClick={() => {
														setBannerToDelete(item);
														setIsOpenToDelete(true);
													}}
													style={{ display: 'none' }}
												>
													<AiOutlineDelete />
												</button>
											</S.actions>
										</td>
									</tr>
								))}
							</tbody>
						</S.table>
					)}
				</S.listContent>
			)}

			{isFormOpen && (
				<FormBannerPopup
					isOpenForm={isFormOpen}
					setIsOpenForm={setIsFormOpen}
					bannerToEdit={bannerToEdit}
					setBannerToEdit={setBannerToEdit}
					setListBanners={setListBanners}
					listBanners={listBanners}
				/>
			)}

			<ConfirmScreen
				title="Tela de confirmacao"
				isOpen={isOpenBoxToDelete}
				setIsOpen={setIsOpenToDelete}
				buttonClose={false}
				functioToExecOnClose={() => setBannerToDelete(null)}
				functionToExecOnConfirm={deleteBanner}
				message={
					<p>
						Tem certeza que deseja deletar o Banner PopUp
						<b> {` "${bannerToDelete?.name || 'Sem nome'}"`}</b>?
					</p>
				}
			/>

			<Modal
				isOpenProps={isPreviewOpen}
				setIsOpenModal={() => setIsPreviewOpen(false)}
				title="Preview Banner PopUp"
				buttonClose
				buttonText="Entendi"
			>
				{bannerToPreview && (
					<>
						<S.PreviewImages $variant={bannerToPreview.type}>
							{bannerToPreview.type === 'SIMPLE' ? (
								<S.PreviewImage>
									<p>Imagem</p>
									<img
										src={bannerToPreview.imageUrl}
										alt={`Imagem ${bannerToPreview.title}`}
									/>
								</S.PreviewImage>
							) : (
								<S.PreviewCarousel>
									<Carousel
										items={bannerToPreview.countdownImages.map((item) => ({
											id: item.id,
											imageUrl: item.imageUrl
										}))}
										isAutoPlay
										interval={2500}
										isBtns={false}
										showClicks={false}
										positionContainerBalls={0}
									/>
								</S.PreviewCarousel>
							)}
						</S.PreviewImages>

						<S.table>
							<tbody>
								<tr>
									<td>Nome</td>
									<td>{bannerToPreview.name}</td>
								</tr>
								<tr>
									<td>Título</td>
									<td>{bannerToPreview.title}</td>
								</tr>
								<tr>
									<td>CTAs</td>
									<td>
										{bannerToPreview.ctas?.length
											? bannerToPreview.ctas
													.map((cta) => cta.label || cta.redirectUrl)
													.join(', ')
											: '-'}
									</td>
								</tr>
								<tr>
									<td>Link</td>
									<td>{bannerToPreview.redirectUrl}</td>
								</tr>
								<tr>
									<td>RNE</td>
									<td>{bannerToPreview.rne || '-'}</td>
								</tr>
								<tr>
									<td>Segmentação</td>
									<td>{getSegmentation(bannerToPreview)}</td>
								</tr>
							</tbody>
						</S.table>
					</>
				)}
			</Modal>
		</S.container>
	);
}
